import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import '../contracts/Payroll_AddDeductionInput.ts';
import '../contracts/Payroll_AddDeductionResponse';
import '../contracts/Payroll_DeductionsResponse';

import '../contracts/Payroll_DeleteDeductionResponse';
import type { Payroll_AddDeductionInput } from '../contracts/Payroll_AddDeductionInput';
/**
 * Composable for managing user-level deductions in payroll
 *
 * User deductions are tied to user_id + payroll_period_id
 * Applied at period confirmation when creating despesas
 *
 * Formula: net_payable = gross_total - user_deductions
 */
export function useUserDeductions() {
  const isLoading = ref(false);
  const error = ref(null);

  /**
   * Add a user deduction to a payroll period
   *
   * @param {Object} deductionData
   * @param {number} deductionData.period_id - Payroll period ID
   * @param {string} deductionData.user_id - Cognito sub
   * @param {number} deductionData.amount - Deduction amount (positive)
   * @param {string} deductionData.description - Mandatory description
   * @param {number} [deductionData.despesa_id] - Optional linked expense
   * @param {number} deductionData.franchise_id - Franchise ID
   * @returns {Promise<Object|null>} Created deduction or null
   */
  const addDeduction = async (deductionData: Payroll_AddDeductionInput) => {
    isLoading.value = true;
    error.value = null;

    try {
      log('useUserDeductions', 'addDeduction', 'Adding user deduction', deductionData);

      validate('Payroll_AddDeductionInput', deductionData);
      const response = await httpService.post('/payroll/add_deduction', deductionData, {
        responseContract: 'Payroll_AddDeductionResponse'
      });

      if (response.success) {
        log('useUserDeductions', 'addDeduction', 'Deduction added successfully', response.dados);
        return response.dados;
      } else {
        throw new Error(response.error || 'Falha ao adicionar dedução');
      }
    } catch (err) {
      error.value = err.message;
      log('useUserDeductions', 'addDeduction', 'Error adding deduction', err);
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * List all deductions for a payroll period
   *
   * @param {number} periodId - Payroll period ID
   * @returns {Promise<Array>} List of deductions
   */
  const listDeductionsByPeriod = async (periodId: number) => {
    isLoading.value = true;
    error.value = null;

    try {
      log('useUserDeductions', 'listDeductionsByPeriod', 'Fetching deductions', { periodId });

      const response = await httpService.get(`/payroll/deductions?period_id=${periodId}`, {
        responseContract: 'Payroll_DeductionsResponse'
      });

      if (response.success) {
        log('useUserDeductions', 'listDeductionsByPeriod', 'Deductions fetched', response.dados);
        return response.dados || [];
      } else {
        throw new Error(response.error || 'Falha ao buscar deduções');
      }
    } catch (err) {
      error.value = err.message;
      log('useUserDeductions', 'listDeductionsByPeriod', 'Error fetching deductions', err);
      return [];
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * List deductions for a specific user in a period
   *
   * @param {string} userId - Cognito sub
   * @param {number} periodId - Payroll period ID
   * @returns {Promise<Array>} List of user's deductions
   */
  const listDeductionsByUserAndPeriod = async (userId: any, periodId: any) => {
    isLoading.value = true;
    error.value = null;

    try {
      log('useUserDeductions', 'listDeductionsByUserAndPeriod', 'Fetching user deductions', { userId, periodId });

      const response = await httpService.get(`/payroll/deductions?period_id=${periodId}&user_id=${userId}`, { responseContract: 'Payroll_DeductionsResponse' });

      if (response.success) {
        log('useUserDeductions', 'listDeductionsByUserAndPeriod', 'User deductions fetched', response.dados);
        return response.dados || [];
      } else {
        throw new Error(response.error || 'Falha ao buscar deduções do usuário');
      }
    } catch (err) {
      error.value = err.message;
      log('useUserDeductions', 'listDeductionsByUserAndPeriod', 'Error fetching user deductions', err);
      return [];
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Delete a user deduction
   *
   * @param {number} deductionId - Deduction ID
   * @param {number} franchiseId - Franchise ID for validation
   * @returns {Promise<boolean>} Success status
   */
  const deleteDeduction = async (deductionId: any, franchiseId: any) => {
    isLoading.value = true;
    error.value = null;

    try {
      log('useUserDeductions', 'deleteDeduction', 'Deleting deduction', { deductionId, franchiseId });

      const response = await httpService.delete(`/payroll/delete_deduction/${deductionId}?franchise_id=${franchiseId}`, { responseContract: 'Payroll_DeleteDeductionResponse' });

      if (response.success) {
        log('useUserDeductions', 'deleteDeduction', 'Deduction deleted successfully');
        return true;
      } else {
        throw new Error(response.error || 'Falha ao deletar dedução');
      }
    } catch (err) {
      error.value = err.message;
      log('useUserDeductions', 'deleteDeduction', 'Error deleting deduction', err);
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get total deductions for a user in a period
   *
   * @param {string} userId - Cognito sub
   * @param {number} periodId - Payroll period ID
   * @returns {Promise<number>} Total deductions amount
   */
  const getTotalByUserAndPeriod = async (userId: any, periodId: any) => {
    try {
      log('useUserDeductions', 'getTotalByUserAndPeriod', 'Calculating total', { userId, periodId });

      const deductions = await listDeductionsByUserAndPeriod(userId, periodId);

      const total = deductions.reduce((sum: any, deduction: any) => {
        return sum + parseFloat(deduction.amount || 0);
      }, 0);

      log('useUserDeductions', 'getTotalByUserAndPeriod', 'Total calculated', total);
      return total;
    } catch (err) {
      error.value = err.message;
      log('useUserDeductions', 'getTotalByUserAndPeriod', 'Error calculating total', err);
      return 0;
    }
  };

  /**
   * Get grouped deductions by user for a period
   *
   * @param {number} periodId - Payroll period ID
   * @returns {Promise<Object>} Deductions grouped by user_id
   */
  const getGroupedByUser = async (periodId: number) => {
    try {
      log('useUserDeductions', 'getGroupedByUser', 'Grouping deductions', { periodId });

      const deductions = await listDeductionsByPeriod(periodId);

      const grouped: any = {};
      deductions.forEach((deduction: any) => {
        const userId = deduction.user_id;
        if (!grouped[userId]) {
          grouped[userId] = {
            user_id: userId,
            total: 0,
            items: []
          };
        }
        grouped[userId].total += parseFloat(deduction.amount || 0);
        grouped[userId].items.push(deduction);
      });

      log('useUserDeductions', 'getGroupedByUser', 'Deductions grouped', grouped);
      return grouped;
    } catch (err) {
      error.value = err.message;
      log('useUserDeductions', 'getGroupedByUser', 'Error grouping deductions', err);
      return {};
    }
  };

  return {
    // State
    isLoading,
    error,

    // Methods
    addDeduction,
    listDeductionsByPeriod,
    listDeductionsByUserAndPeriod,
    deleteDeduction,
    getTotalByUserAndPeriod,
    getGroupedByUser
  };
}
